import PropTypes from 'prop-types';

import { Colors } from '@spotahome/ui-library';
import {
  Cell,
  Pie,
  PieChart as RechartPieChart,
  ResponsiveContainer
} from 'recharts';

const CHART_HEIGHT = 400;

export const PIE_RADIUS = 150;
export const MOBILE_PIE_RADIUS = 125;

export const OUTSIDE_LABEL_POSITION = 'outside';
export const INSIDE_LABEL_POSITION = 'inside';

const INSIDE_LABEL_THRESHOLD = 5;

const COLORS = [
  Colors.interaction,
  Colors['secondary-pink'],
  Colors.tertiary,
  Colors['secondary-blue']
];

const renderCustomLabelOutSidePie = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  payload
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);
  const sx = cx + (outerRadius + 10) * cos;
  const sy = cy + (outerRadius + 10) * sin;
  const mx = cx + (outerRadius + 30) * cos;
  const my = cy + (outerRadius + 30) * sin;
  const ex = mx + (cos >= 0 ? 1 : -1) * 10;
  const ey = my;

  return (
    <g>
      <path
        d={`M${sx},${sy}L${mx},${my}L${ex},${ey}`}
        stroke={Colors.dark}
        fill="none"
      />
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey}
        textAnchor={cos >= 0 ? 'start' : 'end'}
        fill={Colors.dark}
      >
        {payload.label}
      </text>
      <text
        x={ex + (cos >= 0 ? 1 : -1) * 12}
        y={ey + 18}
        textAnchor={cos >= 0 ? 'start' : 'end'}
        fill={Colors.dark}
        fontWeight="bold"
      >
        {`${payload.percentage}%`}
      </text>
    </g>
  );
};

const renderCustomLabelInsidePie = ({
  cx,
  cy,
  midAngle,
  outerRadius,
  payload,
  index
}) => {
  const RADIAN = Math.PI / 180;
  const sin = Math.sin(-midAngle * RADIAN);
  const cos = Math.cos(-midAngle * RADIAN);

  const positionFactor = 0.6; // 0.5 would be middle, 1.0 would be at the edge
  const labelRadius = outerRadius * positionFactor;
  const x = cx + labelRadius * cos;
  const y = cy + labelRadius * sin;

  const isDarkSegment = index === 0; // Only the first segment (dark blue) needs light text
  const textColor = isDarkSegment ? Colors.white : Colors.dark;

  if (payload.percentage < INSIDE_LABEL_THRESHOLD) {
    return null;
  }

  return (
    <g>
      <text
        x={x}
        y={y - 8}
        textAnchor="middle"
        dominantBaseline="central"
        fill={textColor}
      >
        {payload.label}
      </text>
      <text
        x={x}
        y={y + 8}
        textAnchor="middle"
        dominantBaseline="central"
        fill={textColor}
        fontWeight="bold"
      >
        {`${payload.percentage}%`}
      </text>
    </g>
  );
};

const PieChart = ({
  data,
  pieRadius = PIE_RADIUS,
  labelPosition = INSIDE_LABEL_POSITION
}) => {
  return (
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      <RechartPieChart width="100%" height="100%">
        <Pie
          data={data}
          labelLine={false}
          outerRadius={pieRadius}
          cx="50%"
          cy="50%"
          dataKey="percentage"
          label={
            labelPosition === INSIDE_LABEL_POSITION
              ? renderCustomLabelInsidePie
              : renderCustomLabelOutSidePie
          }
        >
          {data.map((_, index) => (
            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
          ))}
        </Pie>
      </RechartPieChart>
    </ResponsiveContainer>
  );
};

PieChart.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      percentage: PropTypes.number.isRequired
    })
  ).isRequired,
  pieRadius: PropTypes.oneOf([PIE_RADIUS, MOBILE_PIE_RADIUS]),
  labelPosition: PropTypes.oneOf([
    OUTSIDE_LABEL_POSITION,
    INSIDE_LABEL_POSITION
  ])
};

export default PieChart;
